import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./AISidebarAsk.css";
import "../../css/Icons.css";
import {
  SyncProgress,
  selectAIContext,
  selectCollections,
  selectEditingCollection,
  selectLinkRecords,
  selectMergeConnected,
  selectOnDiscardConfirm,
  selectSidebarSnippet,
  selectUIStates,
  selectUser,
  setAIContext,
  setEditingCollection,
  setSidebarSnippet,
  setUIStates,
} from "../../redux/features/appData";
import {
  AIContext,
  AIResponseSave,
  Collection,
  LinkRecord,
  getStoreLinkRecord,
  getLinkRecordIdsFromContext,
  Snippet,
  SnippetSimilarity,
  determineQuestionCountString,
  Employee,
} from "winslow-core";
import {
  apiAIAskQuestion,
  apiAISaveResponse,
  aiAIGetResponseSave,
  apiGetSnippets,
  apiGenerateSnippet,
} from "../../services/AIService";
import useWebSocket from "react-use-websocket";

import { LinkRecordActivity } from "../shares/LinkRecordActivity";
import AIAnswer from "../shares/AIAnswer";
import { apiGetLinkRecord } from "../../services/LinkRecordService";
import { CollectionResourcesZero } from "../collections/CollectionResourcesZero";
import { SnippetSidebar } from "../snippets/SnippetSidebar";
import { SnippetPicker } from "../snippets/SnippetPicker";

import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import ModalSnippetDiscardChanges from "../snippets/ModalSnippetDiscardChanges";
import { useAccount } from "../../hooks/useAccount";
import ModalBasicConfirm from "../common/ModalBasicConfirm";
import { selectAccountInfo } from "../../redux/features/account/accountSlice";
import { apiGetCollection } from "../../services/CollectionService";
import { EmployeeSelector } from "../common/EmployeeSelector";

import config from "environment";
import { PersonaEmployeeAutocomplete } from "../common/PersonaEmployeeAutocomplete";
import store from "../../redux/store";
import { useAIStream } from "../../hooks/useAIStream";
import { Button } from "../../ds2/components";

export const NEW_SNIPPET_ID = "__new__";

const sidebarOverride = false;

type AISidebarAskProps = {
  collectionId?: string;
  aiResponseSaveId?: string | undefined;
  queryStringQuestion: string | null;
  employeeId?: string | null;
  hideContext?: boolean;
  collectionName?: string;
  hideQuickReplies?: boolean;
  subtitle?: string;
};

export const emptyAIContext: AIContext = {
  linkRecordId: undefined,
  message: "",
  prompts: undefined,
  presetQuestion: undefined,
  readyToProcess: false,
};

export default function AIAsk({
  collectionId,
  aiResponseSaveId,
  queryStringQuestion,
  employeeId,
  hideContext = false,
  collectionName = undefined,
  hideQuickReplies = false,
  subtitle = "Ask your documents a question.",
}: AISidebarAskProps) {
  const { sendJsonMessage, lastJsonMessage } = useWebSocket(config.wsAiUrl, {
    onOpen: () => console.log("[websocket] opened"),
    onClose: (code: any) => console.log("[websocket] closed: ", code),
    shouldReconnect: (closeEvent) => true,
    onError: (event: WebSocketEventMap["error"]) =>
      console.log("[websocket] error: ", event),
  });

  const dispatch = useDispatch();
  const acct = useAccount();
  const storeUser = useSelector(selectUser);
  const linkRecords = useSelector(selectLinkRecords);
  const aiContext = useSelector(selectAIContext);
  const collections = useSelector(selectCollections);
  const uiStates = useSelector(selectUIStates);
  const mergeConnected = useSelector(selectMergeConnected);
  const sidebarSnippet = useSelector(selectSidebarSnippet);
  const onDiscardConfirm = useSelector(selectOnDiscardConfirm);

  const [question, setQuestion] = useState<string>("");
  const [thinking, setThinking] = useState<boolean>(false);
  const [aiResponseQuestion, setAIResponseQuestion] = useState<string>("");
  const [aiResponse, setAIResponse] = useState<string | null>(null);

  const [aiLinkRecords, setAILinkRecords] = useState<LinkRecord[] | null>(null);
  const [aiSnippets, setAISnippets] = useState<SnippetSimilarity[] | null>(
    null
  );
  const [aiResponseSave, setAIResponseSave] = useState<AIResponseSave | null>(
    null
  );
  const [accountLocked, setAccountLocked] = useState<boolean>(false);
  const [questionCountDiff, setQuestionCountDiff] = useState<string>("");

  const [collectionIsEmpty, setCollectionIsEmpty] = useState<boolean>(true);

  const accountInfo = useSelector(selectAccountInfo);
  const editingCollection = useSelector(selectEditingCollection);

  const handleAskCalledRef = useRef(false);

  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);

  const [initialLoadComplete, setInitialLoadComplete] =
    useState<boolean>(false);

  const { isStreaming, sendMessage } = useAIStream({
    onStreamStart: () => {
      setThinking(true);
      setAIResponse(null);
      setAILinkRecords([]);
      setAIResponseSave(null);
      setAISnippets(null);
      dispatch(setUIStates({ ...uiStates, snippetSidebarOpen: false }));
      dispatch(setSidebarSnippet(null));
    },
    onStreamComplete: async (response) => {
      setThinking(false);
      if (response && storeUser && collectionId) {
        setAIResponse(response.content);

        const linkRecords = await loadResponseLinkRecords(
          response.sources || []
        );
        setAILinkRecords(linkRecords);

        const aiSavedResponse = await apiAISaveResponse({
          question: question,
          response: response.content,
          userId: storeUser?.id ?? null,
          employeeId: selectedEmployee ?? null,
          employeeRecordId: null,
          originatorEmail: undefined,
          originatorName: undefined,
          workspaceId: storeUser!.currentWorkspaceId!,
          collectionId: collectionId,
          linkRecordIds: response.sources || [],
          feedback: undefined,
          sources: response.sources || [],
        });

        if (aiSavedResponse) {
          acct.increment(storeUser.id);
          setAIResponseSave(aiSavedResponse);
        }

        dispatch(
          setAIContext({
            ...aiContext,
            readyToProcess: false,
          })
        );

        handleAskCalledRef.current = false;

        toast("Getting Quick Replies...", toastSuccess);
        await loadSnippets(question, linkRecords);
      }
    },
    onStreamError: (error) => {
      console.error("Stream error:", error);
      toast.error("Error communicating with AI");
    },
  });

  useEffect(() => {
    if (employeeId) {
      setSelectedEmployee(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    if (aiContext.presetQuestion && aiContext.presetQuestion.length > 0) {
      setQuestion(aiContext.presetQuestion);
    }
  }, [aiContext.presetQuestion]);

  useEffect(() => {
    if (aiContext.readyToProcess) {
      if (!handleAskCalledRef.current) {
        handleAskCalledRef.current = true;
        handleAsk();
      }
    }
  }, [aiContext.readyToProcess]);

  useEffect(() => {
    if (!initialLoadComplete && queryStringQuestion) {
      setQuestion(queryStringQuestion);
      if (!handleAskCalledRef.current) {
        handleAskCalledRef.current = true;
        handleAsk();
      }
      setInitialLoadComplete(true);
    }

    if (storeUser && collectionId) {
      (async () => {
        dispatch(setEditingCollection(await apiGetCollection(collectionId)));
      })();
    }

    if (storeUser) {
      acct.loadAccountInfo(storeUser?.id);
    }
    if (storeUser && aiResponseSaveId && !aiResponseSave) {
      (async () => {
        const response = await aiAIGetResponseSave(aiResponseSaveId);

        if (response) {
          setAIResponse(response.response);
          setAIResponseSave(response);

          const linkRecords = await loadResponseLinkRecords(
            response.linkRecordIds
          );
          await loadSnippets(response?.question, linkRecords);
        }
      })();
    }
  }, [queryStringQuestion]);

  useEffect(() => {
    setCollectionIsEmpty(
      editingCollection
        ? !(
            editingCollection.linkRecords &&
            editingCollection.linkRecords.length > 0
          )
        : true
    );
  }, [editingCollection]);

  const loadSnippets = async (
    theQuestion: string,
    linkRecords: LinkRecord[]
  ) => {
    // get snippets
    if (collectionId) {
      const getSnippetsResponse = await apiGetSnippets({
        collectionId,
        userId: storeUser!.id,
        question: theQuestion,
        recipientName: "[Name]",
      });
      if (getSnippetsResponse) {
        setAISnippets(getSnippetsResponse);
      }
    }
  };

  const loadResponseLinkRecords = async (
    linkRecordIds: string[]
  ): Promise<LinkRecord[]> => {
    const filterUnique = (value: string, index: number, self: string[]) =>
      index === self.findIndex((t) => t === value);

    const promises = linkRecordIds
      .filter(filterUnique)
      .map((linkRecordId: string) => apiGetLinkRecord(linkRecordId));

    const linkRecords = await Promise.all(promises);
    const finalLinkRecords: LinkRecord[] = [];

    // get rid of nulls
    linkRecords.forEach((linkRecord: LinkRecord | null) => {
      if (linkRecord) {
        finalLinkRecords.push(linkRecord);
      }
    });

    return finalLinkRecords;
  };

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    if (lastJsonMessage) {
      //setIsStreaming(true);
      const response = lastJsonMessage as {
        sources?: string[];
        linkRecordIds: string[];
        answer: string;
        stage: "starting" | "complete" | "streaming";
        success: boolean;
      };
      const questionToAsk =
        !initialLoadComplete && queryStringQuestion
          ? queryStringQuestion
          : question;
      let theQuestion = questionToAsk;
      if (
        (!theQuestion || theQuestion.length < 1) &&
        aiContext.presetQuestion
      ) {
        theQuestion = aiContext.presetQuestion;
      }

      if (response.stage === "starting") {
        setAIResponseQuestion(theQuestion);
        setAIResponse(response.answer);
        const sourceMap: Map<string, boolean> = new Map<string, boolean>();
        const finalSources: string[] = [];
        setThinking(false);
        response.linkRecordIds?.forEach((source: string) => {
          if (!sourceMap.has(source)) {
            sourceMap.set(source, true);
            finalSources.push(source);
          }
        });
        const filterUnique = (value: string, index: number, self: string[]) =>
          index === self.findIndex((t) => t === value);
        const promises = response.linkRecordIds
          .filter(filterUnique)
          .map((linkRecordId: string) => apiGetLinkRecord(linkRecordId));

        (async () => {
          const linkRecords = await Promise.all(promises);
          const finalLinkRecords: LinkRecord[] = linkRecords.filter(
            (lr): lr is LinkRecord => lr !== null
          );

          setAILinkRecords(finalLinkRecords);
        })();
      }
      //update the aiResponse
      else if (response.stage === "streaming") {
        setAIResponse((prev) => {
          const newValue = (prev || "") + response.answer;
          return newValue;
        });
      } else if (response.stage === "complete") {
        //        setIsStreaming(false);
        setAIResponse(response.answer);
        if (!storeUser) return;
        if (storeUser.currentWorkspaceId) {
          apiAISaveResponse({
            question: theQuestion,
            response: response.answer,
            userId: storeUser?.id ?? null,
            employeeId: selectedEmployee ?? null,
            employeeRecordId: null,
            originatorEmail: undefined,
            originatorName: undefined,
            workspaceId: storeUser.currentWorkspaceId,
            collectionId: collectionId!,
            linkRecordIds: response.linkRecordIds,
            feedback: undefined,
            sources: finalLinkRecords.map(
              (linkRecord: LinkRecord) => linkRecord.id
            ),
          }).then((aiSavedResponse) => {
            if (aiSavedResponse) {
              acct.increment(storeUser.id);
            }

            setAIResponseSave(aiSavedResponse);
          });
        }

        setAIContext({
          ...aiContext,
          readyToProcess: false,
        });

        handleAskCalledRef.current = false;

        if (response.success) {
          toast("Getting Quick Replies...", toastSuccess);
          loadSnippets(theQuestion, finalLinkRecords).then(() => {});
        }
      }
    }
  }, [lastJsonMessage]);

  const handleAsk = async () => {
    if (thinking || isStreaming) return;

    const questionToAsk =
      !initialLoadComplete && queryStringQuestion
        ? queryStringQuestion
        : question;

    if (questionToAsk.trim() === "") {
      console.log("question is empty");
      handleAskCalledRef.current = false;
      return;
    }

    if (!storeUser || !linkRecords || !collectionId) {
      handleAskCalledRef.current = false;
      return;
    }

    toast("Asking Winslow...", toastSuccess);
    let theQuestion = questionToAsk;
    if ((!theQuestion || theQuestion.length < 1) && aiContext.presetQuestion) {
      theQuestion = aiContext.presetQuestion;
    }

    sendMessage({
      action: "ask",
      collectionId,
      inputMessage: theQuestion,
      userId: storeUser.id,
      employeeId:
        selectedEmployee === "" || selectedEmployee === null
          ? undefined
          : selectedEmployee,
      personaId:
        selectedPersona === "" || selectedPersona === null
          ? undefined
          : selectedPersona,
    });
  };

  const resetState = () => {
    setThinking(false);
    setQuestion("");
    setAIResponseQuestion("");
    setAIResponse(null);
    setAILinkRecords(null);
    setAIResponseSave(null);
    setAISnippets(null);
  };

  const handleCreateSnippet = async () => {
    if (!aiResponse || !collectionId) {
      return;
    }
    toast("Generating quick reply...", toastSuccess);
    const newSnippetGeneratedResponse = await apiGenerateSnippet({
      collectionId,
      question: question,
      recipientName: "[Name]",
      userId: storeUser!.id,
    });

    if (newSnippetGeneratedResponse) {
      if (newSnippetGeneratedResponse.type === "NEW") {
        const newSnippet = {
          ...newSnippetGeneratedResponse.result,
          collectionId: editingCollection?.id,
          userId: storeUser?.id,
          id: NEW_SNIPPET_ID,
          name: "New Quick Reply Suggestion",
          question,
          similarity: 100,
          linkRecords: aiLinkRecords,
        } as SnippetSimilarity;

        dispatch(setSidebarSnippet(newSnippet));
        dispatch(
          setUIStates({
            ...uiStates,
            snippetSidebarOpen: true,
          })
        );
      } else {
        toast("Something happened during snippet creation...", toastFail);
      }
    } else {
      toast("Unable to create snippet...", toastFail);
    }
  };

  const finalLinkRecords: LinkRecord[] = [];

  const linkRecordIds = getLinkRecordIdsFromContext(aiContext, linkRecords);

  linkRecordIds.forEach((linkRecordId: string) => {
    if (linkRecords) {
      const linkRecord = getStoreLinkRecord(linkRecordId, linkRecords);

      if (linkRecord) {
        finalLinkRecords.push(linkRecord);
      }
    }
  });

  let index = -1;

  if (collections) {
    index = collections.findIndex((collection: Collection) => {
      return collection.id === collectionId;
    });
  }

  return (
    <div id="ai-sidebar">
      <div className="ai-sidebar-container">
        {!thinking && !aiResponse && (
          <>
            <div className="ai-sidebar-image">
              {/* <div className="thinking-container"> */}
              <img src="/icons/winslow-ai-logo.svg" alt="" />
              {/* </div> */}
            </div>
            <div className="ai-sidebar-title">
              <h1>Winslow Q&A</h1>
            </div>
            <div className="ai-sidebar-text">
              {aiContext.message ? aiContext.message : subtitle}
            </div>
            {finalLinkRecords && finalLinkRecords.length > 0 && (
              <div className="ai-sidebar-source-links">
                {finalLinkRecords.map((linkRecord: LinkRecord) => {
                  return (
                    <>
                      {linkRecord && (
                        <LinkRecordActivity
                          linkRecord={linkRecord}
                          minimum={true}
                          showDrag={false}
                        />
                      )}
                    </>
                  );
                })}
              </div>
            )}
          </>
        )}
        {aiResponse && aiLinkRecords && (
          <>
            <AIAnswer
              answer={aiResponse}
              aiLinkRecords={aiLinkRecords}
              question={aiResponseQuestion}
            />
          </>
        )}
        {!thinking && !aiResponse && collectionIsEmpty && (
          <div className="ai-sidebar-zero">
            <CollectionResourcesZero />
          </div>
        )}
        {thinking && (
          <>
            <div className="ai-sidebar-thinking-container">
              <div className="ai-sidebar-image">
                <div className="thinking-container">
                  <img src="/icons/winslow-ai-logo.svg" alt="" />
                </div>
              </div>
              <div className="ai-sidebar-thinking-text">
                Searching {collectionName ? collectionName : "your documents"}{" "}
                for the answer..
              </div>
            </div>
          </>
        )}

        {!thinking &&
          aiSnippets &&
          aiResponse &&
          aiResponseSave &&
          !hideQuickReplies && (
            <SnippetPicker
              snippets={
                aiSnippets ? (aiSnippets as Snippet[]) : ([] as Snippet[])
              }
              onNew={handleCreateSnippet}
            />
          )}

        {!collectionIsEmpty && (
          <div className="ai-sidebar-question-container">
            <div className="ai-sidebar-question">
              <input
                type="text"
                onChange={(e: any) => {
                  setQuestion(e.target.value);
                }}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter") {
                    handleAsk();
                  }
                }}
                value={question}
                placeholder={
                  !editingCollection ||
                  editingCollection.syncStatus === SyncProgress.ERRORED ||
                  editingCollection.syncStatus === SyncProgress.PROCESSING
                    ? "Questions are not available while collection is processing..."
                    : "What do you want to know?"
                }
                disabled={
                  isStreaming ||
                  !editingCollection ||
                  editingCollection.syncStatus === SyncProgress.ERRORED ||
                  editingCollection.syncStatus === SyncProgress.PROCESSING
                }
                autoFocus={true}
              />
            </div>
            <div className="ai-sidebar-question-button">
              <Button disabled={isStreaming} onClick={handleAsk}>
                {isStreaming ? "Asking..." : "Ask"}
              </Button>
              {/* <button disabled={isStreaming} onClick={handleAsk}>
                {isStreaming ? "Asking..." : "Ask"}
              </button> */}
            </div>
            {config.showPersonas && !hideContext && (
              <PersonaEmployeeAutocomplete
                onChange={(value, isPersona) => {
                  if (value === "" || value === null) {
                    setSelectedEmployee(null);
                    setSelectedPersona(null);
                    return;
                  }

                  if (isPersona) {
                    setSelectedPersona(value);
                    setSelectedEmployee(null);
                  } else {
                    setSelectedEmployee(value);
                    setSelectedPersona(null);
                  }
                }}
                selectedEmployee={selectedEmployee}
                selectedPersona={selectedPersona}
                mergeConnected={mergeConnected}
              />
            )}
          </div>
        )}
      </div>
      {(uiStates.snippetSidebarOpen || sidebarOverride) && sidebarSnippet && (
        <div className="ai-sidebar-sidebar">
          <SnippetSidebar />
        </div>
      )}

      {uiStates.confirmDiscardSnippetOpen && (
        <ModalSnippetDiscardChanges
          isOpen={uiStates.confirmDiscardSnippetOpen}
          onClose={() =>
            dispatch(
              setUIStates({
                ...uiStates,
                confirmDiscardSnippetOpen: false,
              })
            )
          }
          onContinue={() => {
            if (onDiscardConfirm) {
              onDiscardConfirm();
            }
          }}
        />
      )}
    </div>
  );
}
